import { fromBase64Url, sha256, toBase64Url } from '../lib/crypto';
import type { Invite } from '../lib/invite';
import type { StoredContact } from '../lib/storage';

export const CONTACT_RENDEZVOUS_WINDOW_MS = 30 * 60 * 1_000;

const encoder = new TextEncoder();
const verificationWords = [
  'anchor', 'apple', 'beacon', 'birch', 'breeze', 'bridge', 'brook', 'cabin',
  'cedar', 'clover', 'coral', 'cove', 'crane', 'dawn', 'delta', 'dune',
  'ember', 'fern', 'ferry', 'finch', 'fjord', 'forest', 'glade', 'harbor',
  'hazel', 'island', 'jade', 'kestrel', 'lagoon', 'lantern', 'lark', 'maple',
  'meadow', 'mint', 'moon', 'moss', 'north', 'ocean', 'olive', 'orchid',
  'otter', 'pebble', 'pine', 'plum', 'quartz', 'rain', 'reed', 'river',
  'robin', 'sail', 'shore', 'spruce', 'star', 'stone', 'sunset', 'swift',
  'tide', 'trail', 'valley', 'willow', 'wind', 'wren', 'yellow', 'zephyr',
] as const;

function randomBase64Url(bytes: number): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function hmac(secret: string, value: string): Promise<Uint8Array<ArrayBuffer>> {
  const key = await crypto.subtle.importKey('raw', fromBase64Url(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(value)));
}

export interface ContactSeed {
  proposalId: string;
  secret: string;
  localIdentity: string;
}

export function createContactSeed(): ContactSeed {
  return {
    proposalId: crypto.randomUUID(),
    secret: randomBase64Url(32),
    localIdentity: randomBase64Url(32),
  };
}

export function createLocalIdentity(): string {
  return randomBase64Url(32);
}

export async function deriveContactId(secret: string): Promise<string> {
  return (await sha256(encoder.encode(`pairferry/contact/id/v1:${secret}`))).slice(0, 22);
}

export async function contactVerificationCode(secret: string): Promise<string> {
  const digest = await hmac(secret, 'pairferry/contact/verification/v1');
  return Array.from(digest.slice(0, 4), (value) => verificationWords[value & 63]).join(' · ');
}

export async function deriveContactInvite(contact: StoredContact, now = Date.now()): Promise<Invite> {
  const windowNumber = Math.floor(now / CONTACT_RENDEZVOUS_WINDOW_MS);
  const roomDigest = await hmac(contact.secret, `pairferry/contact/room/v1:${windowNumber}`);
  const signalingDigest = await hmac(contact.secret, `pairferry/contact/signaling/v1:${windowNumber}`);
  return {
    roomId: toBase64Url(roomDigest.slice(0, 16)),
    secret: toBase64Url(signalingDigest),
  };
}

export async function createContactProof(contact: StoredContact, roomId: string): Promise<string> {
  return toBase64Url(await hmac(contact.secret, `pairferry/contact/auth/v1:${roomId}:${contact.localIdentity}`));
}

export async function verifyContactProof(contact: StoredContact, roomId: string, identity: string, proof: string): Promise<boolean> {
  if (identity !== contact.peerIdentity) return false;
  const expected = toBase64Url(await hmac(contact.secret, `pairferry/contact/auth/v1:${roomId}:${identity}`));
  if (expected.length !== proof.length) return false;
  let difference = 0;
  for (let index = 0; index < expected.length; index += 1) difference |= expected.charCodeAt(index) ^ proof.charCodeAt(index);
  return difference === 0;
}

export function contactWindowRemaining(now = Date.now()): number {
  return CONTACT_RENDEZVOUS_WINDOW_MS - (now % CONTACT_RENDEZVOUS_WINDOW_MS);
}
